import { NavLink, useNavigate } from 'react-router-dom';
import { BarChart3, Boxes, Home, LayoutGrid, LogOut, Megaphone, Settings as SettingsIcon, ShoppingBag, Sparkles } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useShop } from '@/hooks/useShop';
import { useShopPath } from '@/hooks/useShopPath';
import { StoreSwitcher } from './StoreSwitcher';

// Mirror AppSidebar order — see comment there.
const collapsedNav = [
	{ to: '/', label: 'Overview', icon: Home, end: true },
	{ to: '/orders', label: 'Orders', icon: ShoppingBag },
	{ to: '/ads', label: 'Ads', icon: Megaphone },
	{ to: '/channels', label: 'Channels', icon: LayoutGrid },
	{ to: '/inventory', label: 'Inventory', icon: Boxes },
	{ to: '/results', label: 'Results', icon: BarChart3 }
];

/**
 * 60px collapsed sidebar variant, shown while the AI chat is expanded. The
 * store avatar collapses the chat back to the full sidebar; the gradient
 * sparkle re-opens the chat; the main nav stays reachable as icon-only links.
 */
export function AppSidebarCollapsed({ onExpand, onOpenChat }) {
	const navigate = useNavigate();
	const { shop, shops } = useShop();
	const { shopPath } = useShopPath();

	const initials = (shop?.name ?? 'Your shop')
		.split(' ')
		.map((p) => p[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();

	return (
		<aside className="flex h-screen w-[60px] shrink-0 flex-col items-center border-r border-sidebar-border bg-sidebar pb-4 pt-7 text-sidebar-foreground">
			{shops.length > 1 ? (
				<div className="mb-4">
					<StoreSwitcher variant="avatar" />
				</div>
			) : (
				<button
					type="button"
					onClick={onExpand}
					aria-label="Expand sidebar"
					className="mb-4 flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white"
				>
					{initials}
				</button>
			)}

			<nav className="mt-2 flex flex-1 flex-col items-center gap-1">
				{onOpenChat ? (
					<button
						type="button"
						onClick={onOpenChat}
						title="Ask AI"
						aria-label="Ask AI"
						className="mb-1 flex h-9 w-9 items-center justify-center rounded-lg transition-transform hover:scale-105"
					>
						<span className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-unify shadow-sm">
							<Sparkles className="h-4 w-4 text-white" strokeWidth={2.5} />
						</span>
					</button>
				) : null}

				{collapsedNav.map((item) => {
					const Icon = item.icon;
					return (
						<NavLink
							key={item.to}
							to={shopPath(item.to)}
							end={item.end}
							title={item.label}
							className={({ isActive }) =>
								cn(
									'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
									isActive
										? 'bg-sidebar-accent text-sidebar-accent-foreground'
										: 'text-sidebar-foreground hover:bg-sidebar-accent/60'
								)
							}
						>
							<Icon className="h-[18px] w-[18px]" strokeWidth={2} />
						</NavLink>
					);
				})}
			</nav>

			<div className="flex flex-col items-center gap-1">
				<NavLink
					to={shopPath('/settings')}
					title="Settings"
					className={({ isActive }) =>
						cn(
							'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
							isActive
								? 'bg-sidebar-accent text-sidebar-accent-foreground'
								: 'text-sidebar-foreground hover:bg-sidebar-accent/60'
						)
					}
				>
					<SettingsIcon className="h-[18px] w-[18px]" />
				</NavLink>
				<button
					type="button"
					onClick={() => navigate('/logout')}
					aria-label="Sign out"
					className="flex h-9 w-9 items-center justify-center rounded-lg text-sidebar-foreground transition-colors hover:bg-sidebar-accent/60"
				>
					<LogOut className="h-[18px] w-[18px]" />
				</button>
			</div>
		</aside>
	);
}
